/**
 * What the guest app shows, given where "today" falls against the stay.
 *
 * The server decides what a session may read (guest-login mints it with a
 * window claim, and row level security enforces it). This is the same rule
 * worked out on the phone, so the screen can say the right thing with no
 * signal and keep saying it after midnight without a fresh login.
 *
 * Dates are plain calendar days on the island. America/Panama never observes
 * daylight saving, so a day boundary is a fixed UTC minus five.
 */

export const WINDOW_DAYS = 30;
const PANAMA_OFFSET_HOURS = -5;

/**
 * planning  more than 30 days before arrival: Arrive and the island only
 * upcoming  inside the window, before arrival
 * in_house  arrival day to departure day
 * departed  after departure, still inside the window
 * closed    more than 30 days after departure: Arrive and the island only
 */
export type StayPhase = 'planning' | 'upcoming' | 'in_house' | 'departed' | 'closed';

export type AccessUi = {
  phase: StayPhase;
  /** Requests, Mensajes and Traductor need this. Arrive, houses, island, safety, weather never do. */
  personalOpen: boolean;
  /** Whole days from today to arrival. Negative once it has happened. */
  daysToArrival: number;
  /** Whole days from today to departure. Negative once it has happened. */
  daysToDeparture: number;
};

export function panamaToday(now: Date = new Date()): string {
  const shifted = new Date(now.getTime() + PANAMA_OFFSET_HOURS * 3_600_000);
  return shifted.toISOString().slice(0, 10);
}

/**
 * `day` is meant to arrive as a bare "YYYY-MM-DD" (what a Postgres `date` column
 * serializes as), but several places in the guest app build a Date directly from a
 * session's arrives_on/departs_on string, and one malformed value (a time component
 * where none was expected) used to throw "Invalid time value" out of Date#toISOString
 * with nothing catching it (found auditing 2026-09-26, while building this file's own
 * test harness, which had exactly this bug). Every call site below takes the calendar
 * day it starts with and ignores whatever, if anything, follows it, instead of handing
 * `new Date()` a string it cannot parse.
 */
export function dateOnly(day: string): string {
  return day.slice(0, 10);
}

export function addDays(day: string, days: number): string {
  const at = new Date(`${dateOnly(day)}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/** Whole days from a to b. Both are YYYY-MM-DD. */
export function dayDiff(a: string, b: string): number {
  const ms = new Date(`${dateOnly(b)}T00:00:00Z`).getTime() - new Date(`${dateOnly(a)}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
}

export type AccessInput = {
  now?: Date;
  arrivesOn: string;
  departsOn: string;
  cancelled?: boolean;
  /**
   * What the server said at login. When it says the session is public only we
   * believe it even if the clock on the phone disagrees, because the server is
   * the one enforcing it.
   */
  serverWindow?: 'in' | 'out';
};

export function accessUiState(input: AccessInput): AccessUi {
  const today = panamaToday(input.now);
  const opens = addDays(input.arrivesOn, -WINDOW_DAYS);
  const closes = addDays(input.departsOn, WINDOW_DAYS);

  let phase: StayPhase;
  if (today < opens) phase = 'planning';
  else if (today < input.arrivesOn) phase = 'upcoming';
  else if (today <= input.departsOn) phase = 'in_house';
  else if (today <= closes) phase = 'departed';
  else phase = 'closed';

  const insideWindow = phase === 'upcoming' || phase === 'in_house' || phase === 'departed';
  const personalOpen = insideWindow && !input.cancelled && input.serverWindow !== 'out';

  return {
    phase,
    personalOpen,
    daysToArrival: dayDiff(today, input.arrivesOn),
    daysToDeparture: dayDiff(today, input.departsOn),
  };
}
